import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Menu, X } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

interface NavbarProps {
  variant?: 'solid' | 'transparent';
}

export function Navbar({ variant = 'solid' }: NavbarProps) {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (variant !== 'transparent') return;

    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  const isTransparent = variant === 'transparent' && !scrolled;

  const navClasses = `sticky top-0 z-50 border-b transition-all duration-300 ${
    isTransparent
      ? 'bg-gray-950/90 backdrop-blur-sm border-white/10'
      : 'bg-background/95 backdrop-blur-sm'
  }`;

  const linkClasses = isTransparent
    ? 'text-sm text-gray-300 hover:text-white transition-colors'
    : 'text-sm text-muted-foreground hover:text-foreground transition-colors';

  const brandTextClasses = isTransparent
    ? 'text-xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent'
    : 'text-xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent';

  return (
    <nav className={navClasses}>
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="SetOpenClaw" className="h-14 w-14 rounded-xl object-contain" />
            <span className={brandTextClasses}>SetOpenClaw</span>
          </Link>

          {/* Center Nav - Desktop */}
          <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            <Link to="/blog" className={linkClasses + ' font-medium'}>
              {t('nav.blog')}
            </Link>
            <Link to="/docs" className={linkClasses + ' font-medium'}>
              {t('nav.docs')}
            </Link>
          </div>

          {/* Right Actions - Desktop */}
          <div className="hidden md:flex items-center gap-3 h-16">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className={isTransparent ? 'text-gray-300 hover:text-white hover:bg-white/10' : ''}>
                    {t('common.dashboard')}
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className={isTransparent ? 'border-white/20 text-gray-300 hover:text-white hover:bg-white/10' : ''}
                >
                  {t('common.signOut')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className={isTransparent ? 'text-gray-300 hover:text-white hover:bg-white/10' : ''}>
                    {t('common.signIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600">{t('common.getStarted')}</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className={`md:hidden p-2 ${isTransparent ? 'text-white' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileMenuOpen && (
        <div className={`md:hidden border-t p-4 space-y-3 ${
          isTransparent ? 'bg-gray-950 border-white/10' : 'bg-background'
        }`}>
          <Link to="/blog" className={`block py-2 text-sm ${isTransparent ? 'text-gray-400' : 'text-muted-foreground'}`} onClick={() => setMobileMenuOpen(false)}>
            {t('nav.blog')}
          </Link>
          <Link to="/docs" className={`block py-2 text-sm ${isTransparent ? 'text-gray-400' : 'text-muted-foreground'}`} onClick={() => setMobileMenuOpen(false)}>
            {t('nav.docs')}
          </Link>
          <LanguageSwitcher variant="compact" />
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="block py-2 text-sm" onClick={() => setMobileMenuOpen(false)}>
                {t('common.dashboard')}
              </Link>
              <button className="block py-2 text-sm text-destructive" onClick={handleLogout}>
                {t('common.signOut')}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="block py-2 text-sm" onClick={() => setMobileMenuOpen(false)}>
                {t('common.signIn')}
              </Link>
              <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                <Button size="sm" className="w-full">{t('common.getStarted')}</Button>
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
